import { html, nothing, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { BaseElement, customElement } from '../../../components/base-element';
import '../../../components/carousel';
import styles from './auth-card.scss?inline';

// The slider only shows from this width; keep in sync with auth-card.scss.
const WIDE_SCREEN = '(min-width: 1200px)';

type Slide = { id: string; src: string; bg: string };

let slidesRequest: Promise<Slide[]> | undefined;

/**
 * Loads the decorative slide art on first need. The DS library build inlines
 * imported images, so static imports would put ~550KB into every auth page;
 * dynamic imports keep it in its own chunks, fetched only on wide screens.
 * Pro Studio has its own folds background; Analytics, Manager and Contact
 * Center share one (a product ask for Analytics).
 */
const loadSlides = () => {
  slidesRequest ??= Promise.all([
    import('./pro-studio.svg'),
    import('./panel-bg-pro-studio.webp'),
    import('./analytics.svg'),
    import('./manager.svg'),
    import('./panel-bg-manager.webp'),
    import('./contact-center.svg'),
  ]).then(
    ([
      proStudio,
      proStudioBg,
      analytics,
      manager,
      managerBg,
      contactCenter,
    ]) => [
      { id: 'pro-studio', src: proStudio.default, bg: proStudioBg.default },
      { id: 'analytics', src: analytics.default, bg: managerBg.default },
      { id: 'manager', src: manager.default, bg: managerBg.default },
      {
        id: 'contact-center',
        src: contactCenter.default,
        bg: managerBg.default,
      },
    ]
  );
  return slidesRequest;
};

@customElement('ps-auth-card')
export class AuthCardWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @state() private slides?: Slide[];

  private wideScreen?: MediaQueryList;

  private handleWideScreen = (event: MediaQueryListEvent) => {
    if (event.matches) this.showSlides();
  };

  private showSlides() {
    this.wideScreen?.removeEventListener('change', this.handleWideScreen);
    loadSlides().then((slides) => {
      this.slides = slides;
    });
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.slides) return;
    this.wideScreen = window.matchMedia(WIDE_SCREEN);
    if (this.wideScreen.matches) this.showSlides();
    else this.wideScreen.addEventListener('change', this.handleWideScreen);
  }

  disconnectedCallback() {
    this.wideScreen?.removeEventListener('change', this.handleWideScreen);
    super.disconnectedCallback();
  }

  // @todo: restore slotted header & footer containers once Next.js not using dynamic imports
  // ${this.slotted('header')
  // ? html`<slot name="header" class="c-auth-card__header"></slot>`
  // : ''}
  // <slot class="c-auth-card__main"></slot>
  // ${this.slotted('footer')
  // ? html`<slot name="footer" class="c-auth-card__footer"></slot>`
  // : ''}

  render() {
    return html`
      <div
        class=${classMap({
          'c-auth-card': true,
          'c-auth-card--has-cover-img': true,
        })}
      >
        <div class="c-auth-card__content">
          <slot name="header" class="c-auth-card__header"></slot>
          <slot class="c-auth-card__main"></slot>
          <slot name="footer" class="c-auth-card__footer"></slot>
        </div>
        <div class="c-auth-card__slider" aria-hidden="true">
          ${this.slides
            ? html`
                <ps-carousel autoplay>
                  ${this.slides.map(
                    ({ id, src, bg }) => html`
                      <ps-carousel-item>
                        <img
                          src=${src}
                          class="c-auth-card__slider-img"
                          data-slide=${id}
                          style="background-image: url(${bg})"
                          alt=""
                          width="552"
                          height="612"
                        />
                      </ps-carousel-item>
                    `
                  )}
                </ps-carousel>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-auth-card': AuthCardWC;
  }
}
